import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
var _class, _class2, _GuidedRowEditorImpl;
/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { InlinePanelLayout } from "abreact/expression/layout/InlinePanelLayout";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { GuidedExpressionEditor } from "abreact/expression/ui/editor/GuidedExpressionEditor";
import { AdvancedArgumentOperandValueEditorGroupButton } from "abreact/expression/ui/editor/impl/condition/operandValue/operand/argument/AdvancedArgumentOperandValueEditorGroupButton";
import { ArgumentOperandValueEditorGroup } from "abreact/expression/ui/editor/impl/condition/operandValue/operand/argument/ArgumentOperandValueEditorGroup";
import { OptionalArgumentManager } from "abreact/expression/ui/editor/impl/condition/operandValue/operand/argument/OptionalArgumentManager";
import { OptionalArgumentOperandSelector } from "abreact/expression/ui/editor/impl/condition/operandValue/operand/argument/OptionalArgumentOperandSelector";
import { SourceOperandValueEditor, SourceOperandValueEditorWithManagedFocus } from "abreact/expression/ui/editor/impl/condition/operandValue/operand/source/SourceOperandValueEditor";
import { OperatorOperandValueEditor } from "abreact/expression/ui/editor/impl/condition/operandValue/operator/OperatorOperandValueEditor";
import { OperatorOperandValueImpl } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/OperatorOperandValueImpl";
import { GEECompletenessIndicator } from "abreact/expression/ui/editor/impl/utils/GEECompletenessIndicator";
import { GuidedExpressionEditorUtils } from "abreact/expression/ui/editor/impl/utils/GuidedExpressionEditorUtils";
import { AbBooleanLogicTreeContext } from "abreact/incubator/ui/abBooleanLogicTree/AbBooleanLogicTreeContext";
import { AbBooleanLogicTreeFocus } from "abreact/incubator/ui/abBooleanLogicTree/AbBooleanLogicTreeFocus";
import { ABLOGICTREE_SELECTABLE_CLASS } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicTreeSelectionMachine";
import { CssUtils } from "abreact/incubator/ui/utils/CssUtils";
import { AbIcon } from "abreact/misc/AbIcon";
import { AbSize } from "abreact/misc/AbSize";
import { AbAlert } from "abreact/popup/AbAlert";
import { AbVBox } from "absdk/commons/ui/layout/AbVBox";
import { AbButton } from "absdk/commons/ui/misc/AbButton";
import { AlertLevel } from "absdk/commons/ui/popup/AbAlert";
import { Assert } from "abweb/utils/Assert";
import { AbMaybePromise } from "abweb/utils/async/AbMaybePromise";
import { NullUtils } from "abweb/utils/NullUtils";
import Immutable from "immutable";
import { computed, makeObservable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";

/**
 * @author fgracely
 */
let GuidedRowEditorImpl = observer(_class = (_class2 = (_GuidedRowEditorImpl = class GuidedRowEditorImpl extends React.Component {
  constructor(props) {
    super(props);
    this.applySourceOperandValue = this.applySourceOperandValue.bind(this);
    this.applyExpOperator = this.applyExpOperator.bind(this);
    this.applyArgumentOperandValue = this.applyArgumentOperandValue.bind(this);
    this.applyArgumentOperands = this.applyArgumentOperands.bind(this);
    this.showRowDetails = this.showRowDetails.bind(this);
    makeObservable(this);
  }
  get operatorOperandValue() {
    return this.props.rowModel.operatorOperandValue;
  }
  render() {
    const {
      sourceOperandValue,
      argumentOperandValuesByOperandName
    } = this.props.rowModel;
    const {
      operatorOperandValue
    } = this;
    const argumentOperands = operatorOperandValue != null ? GuidedExpressionEditorUtils.getArgumentOperands(this.props.expressionDomainModel, sourceOperandValue, operatorOperandValue.expOperator, false) : [];
    return /*#__PURE__*/React.createElement(OptionalArgumentManager, {
      argumentOperands: argumentOperands,
      operandValuesByOperandName: argumentOperandValuesByOperandName,
      isReadonly: this.props.isReadonly,
      applyArgumentOperands: this.applyArgumentOperands
    }, /*#__PURE__*/React.createElement(InlinePanelLayout.Container, {
      className: CssUtils.composeClassNames("ab-gee-expression-editor-condition", ABLOGICTREE_SELECTABLE_CLASS, this.props.disabled && "ab-disabled"),
      dataTestId: this.props.dataTestId
    }, this.getRowPrefix(), this.sourceOperandValueEditor, this.operatorOperandValueEditor, this.renderArgumentOperandValueEditorGroup(argumentOperands), this.advancedArgumentOperandValueGroupEditorButton, this.showRowDetailsButton, this.validationIndicator, this.renderOptionalArgumentsButton(argumentOperands)));
  }
  getRowPrefix() {
    return this.props.conditionPrefixFunction ? this.props.conditionPrefixFunction(this.props) : null;
  }
  componentDidMount() {
    if (this.operatorOperandValue == null && this.operatorElementOptions?.length === 1) {
      const [{
        expOperator
      }] = this.operatorElementOptions;
      this.applyExpOperator(expOperator, Immutable.Set() /* No flags selected */, {
        frameworkInitiated: true
      });
    }
  }
  get sourceOperandValueEditor() {
    const {
      rowModel,
      expressionDomainModel
    } = this.props;
    const baseProps = {
      operandName: rowModel.sourceOperandValue?.operandName ?? expressionDomainModel.defaultSourceOperandName,
      operandValue: rowModel.sourceOperandValue,
      operatorOperandValue: this.operatorOperandValue,
      liveViewSliceModel: this.props.liveViewSliceModel,
      expressionDomainModel: expressionDomainModel,
      componentFactory: this.props.componentFactory,
      expTemplateConfiguration: this.props.expTemplateConfiguration,
      dataTestId: this.props.dataTestId,
      generateId: this.props.generateId,
      rowKey: this.props.rowKey
    };
    let sourceOperandValueEditor;
    if (this.props.isReadonly || this.props.rowModel.sourceOperandValue?.isEditable === false) {
      sourceOperandValueEditor = /*#__PURE__*/React.createElement(SourceOperandValueEditor, _extends({
        isReadonly: true
      }, baseProps));
    } else {
      sourceOperandValueEditor = /*#__PURE__*/React.createElement(SourceOperandValueEditorWithManagedFocus, _extends({
        isReadonly: false
      }, baseProps, {
        customizationFunctions: this.props.customizationFunctions,
        applyOperandValue: this.applySourceOperandValue
      }));
    }
    return /*#__PURE__*/React.createElement(InlinePanelLayout.Item, {
      className: "ab-gee-operand-value-editor-container"
    }, sourceOperandValueEditor);
  }
  applySourceOperandValue(operandValueMaybePromise, context) {
    Assert.assert(!this.props.isReadonly);
    const rowModelMaybePromise = AbMaybePromise.map(operandValueMaybePromise, operandValue => {
      let {
        rowModel
      } = this.props;
      if (!GuidedExpressionEditor.AbstractModel.AbstractRowModel.AbstractOperandValue.equals(operandValue, this.props.rowModel.sourceOperandValue)) {
        rowModel = this.computeUpdatedRowModelGivenSourceOperandValue(rowModel, operandValue);
      }
      return rowModel;
    });
    this.props.applyRowModel(rowModelMaybePromise, context);
  }
  computeUpdatedRowModelGivenSourceOperandValue(rowModel, sourceOperandValue) {
    Assert.assert(!this.props.isReadonly);
    const oldSourceOperandValue = rowModel.sourceOperandValue;
    rowModel = rowModel.withSourceOperandValue(sourceOperandValue);
    const operatorOperandValue = rowModel.operatorOperandValue;
    let argumentOperandValuesByOperandName = Immutable.Map();
    if (sourceOperandValue != null) {
      const operatorElementOptions = this.props.expressionDomainModel.determineApplicableOperatorsBasedOnAcceptedSourceValueTypes(NullUtils.getRequiredPropertyValue(sourceOperandValue, "valueCategoryID"), NullUtils.getRequiredPropertyValue(sourceOperandValue, "valueTypeIDs"), sourceOperandValue.multiple ?? false, OperatorOperandValueImpl.isInstance(sourceOperandValue), this.props.operatorRoleID, sourceOperandValue.typeQualifier);
      if (operatorOperandValue == null) {
        if (operatorElementOptions.length === 1) {
          const [{
            expOperator
          }] = operatorElementOptions;
          rowModel = this.computeUpdatedRowModelGivenExpOperator(rowModel, operatorOperandValue, expOperator, Immutable.Set() /* No flags selected */);
        }
      } else {
        const {
          expOperator
        } = operatorOperandValue;
        if (operatorElementOptions.find(({
          expOperator: {
            id
          }
        }) => id === expOperator.id) != null) {
          if (this.props.customizationFunctions?.retainArgumentOperandValues != null) {
            argumentOperandValuesByOperandName = this.props.customizationFunctions.retainArgumentOperandValues(expOperator, expOperator, sourceOperandValue, rowModel.argumentOperandValuesByOperandName, this.props.expressionDomainModel, oldSourceOperandValue);
          }
        } else {
          rowModel = rowModel.withOperatorOperandValue(undefined);
        }
      }
    } else {
      rowModel = rowModel.withOperatorOperandValue(undefined);
    }
    rowModel = rowModel.withArgumentOperandValuesByOperandName(argumentOperandValuesByOperandName);
    return rowModel;
  }

  /**
   * This is necessary in the case where the state is restored from data and not via user gestures.
   */
  get operatorElementOptions() {
    const {
      sourceOperandValue
    } = this.props.rowModel;
    if (sourceOperandValue != null) {
      const operatorElementOptions = this.props.expressionDomainModel.determineApplicableOperatorsBasedOnAcceptedSourceValueTypes(NullUtils.getRequiredPropertyValue(sourceOperandValue, "valueCategoryID"), NullUtils.getRequiredPropertyValue(sourceOperandValue, "valueTypeIDs"), sourceOperandValue.multiple ?? false, OperatorOperandValueImpl.isInstance(sourceOperandValue), this.props.operatorRoleID, sourceOperandValue.typeQualifier);
      return operatorElementOptions.filter(({
        isValid
      }) => isValid);
    }
  }
  get operatorOperandValueEditor() {
    if (this.props.rowModel.sourceOperandValue == null) {
      return this.editorPlaceholder("ab-gee-operator-operand-value-editor-container");
    }
    const {
      operatorOperandValue,
      operatorElementOptions
    } = this;
    if (operatorElementOptions == null) {
      return null;
    }
    const baseProps = {
      sourceOperandValue: this.props.rowModel.sourceOperandValue,
      operatorOperandValue: operatorOperandValue,
      operatorElementOptions: operatorElementOptions,
      expressionDomainModel: this.props.expressionDomainModel,
      componentFactory: this.props.componentFactory,
      dataTestId: this.props.dataTestId
    };
    let operatorOperandValueEditor;
    if (this.props.isReadonly || operatorOperandValue?.isEditable === false) {
      operatorOperandValueEditor = /*#__PURE__*/React.createElement(OperatorOperandValueEditor, _extends({
        isReadonly: true
      }, baseProps));
    } else {
      operatorOperandValueEditor = /*#__PURE__*/React.createElement(OperatorOperandValueEditor, _extends({
        isReadonly: false
      }, baseProps, {
        allowUnset: false,
        onChange: this.applyExpOperator
      }));
    }
    return /*#__PURE__*/React.createElement("span", {
      className: "ab-gee-operator-operand-value-editor-container"
    }, operatorOperandValueEditor);
  }
  applyExpOperator(expOperator, executionFlags, context) {
    Assert.assert(!this.props.isReadonly);
    let {
      rowModel
    } = this.props;
    const operatorOperandValue = rowModel.operatorOperandValue;
    if (this.hasChanged(expOperator, operatorOperandValue, executionFlags)) {
      rowModel = this.computeUpdatedRowModelGivenExpOperator(rowModel, operatorOperandValue, expOperator, executionFlags);
      this.handleOperatorOnSelectFocus(expOperator, rowModel);
      this.props.applyRowModel(rowModel, context);
    }
  }
  applyArgumentOperands(argumentOperandsByOperandName) {
    Assert.assert(!this.props.isReadonly);
    const {
      rowModel
    } = this.props;
    this.props.applyRowModel(rowModel.withArgumentOperandValuesByOperandName(argumentOperandsByOperandName));
  }
  handleOperatorOnSelectFocus(expOperator, rowModel) {
    const argumentOperands = GuidedExpressionEditorUtils.getArgumentOperands(this.props.expressionDomainModel, rowModel.sourceOperandValue, expOperator);
    const focusManager = this.context.focusManager;
    if (focusManager != null) {
      if (argumentOperands.length > 0) {
        /* Set focus target to be first Argument Operand */
        const argOne = argumentOperands[0];
        focusManager.setFocusRequest(new AbBooleanLogicTreeFocus.ArgTarget(this.props.rowKey, argOne.argumentIdentifier));
      }
    }
  }
  hasChanged(expOperator, operatorOperandValue, executionFlags) {
    const originalFlags = operatorOperandValue?.executionFlags ?? Immutable.Set();
    const intersectedExecutionFlags = originalFlags.intersect(executionFlags);
    return operatorOperandValue?.expOperator?.id !== expOperator.id || intersectedExecutionFlags.size !== executionFlags.size || intersectedExecutionFlags.size !== originalFlags.size;
  }
  computeUpdatedRowModelGivenExpOperator(rowModel, operatorOperandValue, expOperator, executionFlags) {
    Assert.assert(!this.props.isReadonly);
    const partialStateFromExpOperator = GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperatorOperandValue.partialStateFromExpOperator(expOperator, this.props.expressionDomainModel);
    const partialState = GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperatorOperandValue.partialState({
      executionFlags: executionFlags,
      ...partialStateFromExpOperator
    });
    let argumentOperandValuesByOperandName = Immutable.Map();
    if (operatorOperandValue != null) {
      const oldExpOperator = operatorOperandValue.expOperator;
      if (oldExpOperator.id === expOperator.id) {
        argumentOperandValuesByOperandName = rowModel.argumentOperandValuesByOperandName;
      } else if (oldExpOperator.argumentOperands?.length === expOperator.argumentOperands?.length) {
        if (this.props.customizationFunctions?.retainArgumentOperandValues != null) {
          const sourceOperandValue = NullUtils.assertDefined(rowModel.sourceOperandValue);
          argumentOperandValuesByOperandName = this.props.customizationFunctions.retainArgumentOperandValues(oldExpOperator, expOperator, sourceOperandValue, rowModel.argumentOperandValuesByOperandName, this.props.expressionDomainModel, sourceOperandValue);
        }
      }
      operatorOperandValue = operatorOperandValue.with(partialState);
    } else {
      operatorOperandValue = new OperatorOperandValueImpl({
        id: rowModel.id,
        ...partialState
      });
    }
    rowModel = rowModel.withArgumentOperandValuesByOperandName(argumentOperandValuesByOperandName);
    return rowModel.withOperatorOperandValue(operatorOperandValue);
  }
  renderArgumentOperandValueEditorGroup(argumentOperands) {
    const {
      sourceOperandValue,
      argumentOperandValuesByOperandName
    } = this.props.rowModel;
    const {
      operatorOperandValue
    } = this;
    if (sourceOperandValue == null || operatorOperandValue == null) {
      return this.editorPlaceholder("ab-gee-operand-value-editor-container");
    }
    if (argumentOperands.length === 0) {
      return this.editorPlaceholder("ab-gee-operand-value-editor-container", "ab-gee-source-operand-box-only");
    }
    const baseProps = {
      sourceOperandValue: sourceOperandValue,
      operatorOperandValue: operatorOperandValue,
      operandValuesByOperandName: argumentOperandValuesByOperandName,
      argumentOperands: argumentOperands,
      liveViewSliceModel: this.props.liveViewSliceModel,
      isAdvanced: false,
      expressionDomainModel: this.props.expressionDomainModel,
      componentFactory: this.props.componentFactory,
      dataTestId: this.props.dataTestId,
      expTemplateConfiguration: this.props.expTemplateConfiguration,
      generateId: this.props.generateId,
      rowKey: this.props.rowKey
    };
    if (this.props.isReadonly) {
      return /*#__PURE__*/React.createElement(ArgumentOperandValueEditorGroup, _extends({
        isReadonly: true
      }, baseProps));
    } else {
      return /*#__PURE__*/React.createElement(ArgumentOperandValueEditorGroup, _extends({
        isReadonly: false
      }, baseProps, {
        customizationFunctions: this.props.customizationFunctions,
        applyOperandValue: this.applyArgumentOperandValue
      }));
    }
  }
  renderOptionalArgumentsButton(argumentOperands) {
    return this.props.isReadonly ? /*#__PURE__*/React.createElement(React.Fragment, null) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      style: {
        flexBasis: "100%"
      }
    }), /*#__PURE__*/React.createElement(OptionalArgumentOperandSelector, {
      argumentOperands: argumentOperands,
      dataTestIdPrefix: this.props.dataTestId ?? ""
    }));
  }
  get advancedArgumentOperandValueGroupEditorButton() {
    const {
      sourceOperandValue,
      argumentOperandValuesByOperandName
    } = this.props.rowModel;
    const {
      operatorOperandValue
    } = this;
    if (sourceOperandValue == null || operatorOperandValue == null) {
      return null;
    }
    const advancedArgumentOperands = GuidedExpressionEditorUtils.getArgumentOperands(this.props.expressionDomainModel, sourceOperandValue, operatorOperandValue.expOperator, true);
    const baseProps = {
      sourceOperandValue: sourceOperandValue,
      operatorOperandValue: operatorOperandValue,
      advancedArgumentOperands: advancedArgumentOperands,
      liveViewSliceModel: this.props.liveViewSliceModel,
      operandValuesByOperandName: argumentOperandValuesByOperandName,
      expressionDomainModel: this.props.expressionDomainModel,
      componentFactory: this.props.componentFactory,
      dataTestId: this.props.dataTestId,
      expTemplateConfiguration: this.props.expTemplateConfiguration,
      generateId: this.props.generateId
    };
    if (this.props.isReadonly) {
      return /*#__PURE__*/React.createElement(AdvancedArgumentOperandValueEditorGroupButton, _extends({
        isReadonly: true
      }, baseProps));
    } else {
      return /*#__PURE__*/React.createElement(AdvancedArgumentOperandValueEditorGroupButton, _extends({
        isReadonly: false
      }, baseProps, {
        customizationFunctions: this.props.customizationFunctions,
        applyOperandValue: this.applyArgumentOperandValue
      }));
    }
  }
  applyArgumentOperandValue(operandName, operandValueMaybePromise, context) {
    Assert.assert(!this.props.isReadonly);
    const rowModelMaybePromise = AbMaybePromise.map(operandValueMaybePromise, operandValue => {
      let {
        rowModel
      } = this.props;
      if (!GuidedExpressionEditor.AbstractModel.AbstractRowModel.AbstractOperandValue.equals(operandValue, rowModel.argumentOperandValuesByOperandName.get(operandName))) {
        rowModel = rowModel.withArgumentOperandValue(operandName, operandValue);
        if (operandValue == null) {
          rowModel = this.clearDependentArgumentOperands(operandName, rowModel);
        }
      }
      return rowModel;
    });
    this.props.applyRowModel(rowModelMaybePromise, context);
  }
  clearDependentArgumentOperands(operandName, rowModel) {
    if (rowModel.operatorOperandValue == null) {
      return rowModel;
    }
    const expOperator = rowModel.operatorOperandValue.expOperator;
    // Compute dependency name for current operand
    const currentArgumentOperand = this.props.expressionDomainModel.getExpArgumentOperandForExpOperator(expOperator.id, operandName);
    // Clear out any dependent arguments
    const argumentOperands = rowModel.operatorOperandValue.expOperator.argumentOperands ?? [];
    for (const operand of argumentOperands) {
      const group = this.props.expressionDomainModel.findExpArgumentOperandGroup(expOperator.id, operand.groupID);
      if (GuidedExpressionEditorUtils.Dependencies.isDependentOn(operand, group, currentArgumentOperand)) {
        rowModel = rowModel.withArgumentOperandValue(operand.name, undefined);
      }
    }
    return rowModel;
  }
  get showRowDetailsButton() {
    if (!this.props.isReadonly && this.props.debug) {
      return /*#__PURE__*/React.createElement(AbButton, {
        className: "ab-no-border-button",
        icon: "absdk:info-fill",
        onClick: this.showRowDetails
      });
    }
  }
  showRowDetails() {
    let rowDetails;
    const {
      rowModel
    } = this.props;
    if (rowModel != null) {
      rowDetails = /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("pre", null, AbReactResources.expressionEditor_selectedOperatorInfo()), /*#__PURE__*/React.createElement("pre", null, JSON.stringify(rowModel, null, 2)));
    }
    AbAlert.show({
      level: AlertLevel.INFO,
      title: AbReactResources.expression_debug_rowDetails(),
      message: /*#__PURE__*/React.createElement(AbVBox, {
        spacing: true
      }, rowDetails)
    });
  }
  get validationIndicator() {
    if (!this.props.rowModel.isComplete()) {
      return /*#__PURE__*/React.createElement(GEECompletenessIndicator, {
        isComplete: this.props.rowModel.isComplete(),
        tooltip: AbReactResources.expression_incomplete_condition_tooltip(),
        dataTestId: this.props.dataTestId
      });
    } else if (this.props.liveViewSliceModel?.findEvaluationResult(this.props.rowKey) === "ERROR") {
      const testDataResult = this.props.liveViewSliceModel.findTestDataResult(this.props.rowKey);
      const tooltip = testDataResult?.value ? AbReactResources.abBooleanLogicTree_runtimeErr_tooltipMessage(testDataResult?.value) : AbReactResources.abBooleanLogicTree_runtimeErr_tooltip();
      return /*#__PURE__*/React.createElement("span", {
        className: "ab-gee-validation-indicator-container"
      }, /*#__PURE__*/React.createElement(AbIcon, {
        image: "absdk:error",
        className: "ab-gee-runtime-error-indicator",
        dataTestId: `${this.props.dataTestId}-runtime-error-indicator`,
        size: AbSize.MEDIUM,
        tooltip: tooltip
      }));
    }
    return /*#__PURE__*/React.createElement("span", {
      className: "ab-gee-validation-indicator-container ab-gee-placeholder"
    });
  }
  editorPlaceholder(...classNames) {
    // `display: none;` by default
    return /*#__PURE__*/React.createElement("div", {
      className: CssUtils.composeClassNames("ab-gee-placeholder", ...classNames)
    });
  }
}, _defineProperty(_GuidedRowEditorImpl, "contextType", AbBooleanLogicTreeContext), _GuidedRowEditorImpl), _applyDecoratedDescriptor(_class2.prototype, "operatorOperandValue", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "operatorOperandValue"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "sourceOperandValueEditor", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "sourceOperandValueEditor"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "operatorElementOptions", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "operatorElementOptions"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "operatorOperandValueEditor", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "operatorOperandValueEditor"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "advancedArgumentOperandValueGroupEditorButton", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "advancedArgumentOperandValueGroupEditorButton"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "showRowDetailsButton", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "showRowDetailsButton"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "validationIndicator", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "validationIndicator"), _class2.prototype), _class2)) || _class;
export class GuidedRowEditor extends React.Component {
  constructor(props) {
    super(props);
    this.applyRowModel = this.applyRowModel.bind(this);
  }
  render() {
    const baseProps = {
      rowModel: this.props.rowModel,
      operatorRoleID: this.props.operatorRoleID,
      liveViewSliceModel: this.props.liveViewSliceModel,
      expressionDomainModel: this.props.expressionDomainModel,
      componentFactory: this.props.componentFactory,
      dataTestId: this.props.dataTestId,
      disabled: this.props.disabled,
      expTemplateConfiguration: this.props.expTemplateConfiguration,
      debug: this.props.debug,
      generateId: this.props.generateId,
      rowKey: this.props.rowKey,
      rowPositionInGroup: this.props.rowPositionInGroup,
      conditionPrefixFunction: this.props.conditionPrefixFunction
    };
    if (this.props.isReadonly) {
      return /*#__PURE__*/React.createElement(GuidedRowEditorImpl, _extends({
        isReadonly: true
      }, baseProps));
    } else {
      return /*#__PURE__*/React.createElement(GuidedRowEditorImpl, _extends({
        isReadonly: false
      }, baseProps, {
        customizationFunctions: this.props.customizationFunctions,
        applyRowModel: this.applyRowModel
      }));
    }
  }
  applyRowModel(rowModelMaybePromise, context) {
    Assert.assert(!this.props.isReadonly);
    this.props.applyRowModel(this.props.rowKey, rowModelMaybePromise, context);
  }
}
//# sourceMappingURL=GuidedRowEditor.js.map
